import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
} from 'react'
import { useTheme, type ThemeMode } from '@/contexts/ThemeContext'

const TV_SCRIPT_SRC = 'https://s3.tradingview.com/tv.js'
let scriptPromise: Promise<void> | null = null

const THEME_COLORS = {
  dark: {
    theme: 'dark' as const,
    toolbar_bg: '#0b0e11',
    background: '#0b0e11',
  },
  light: {
    theme: 'light' as const,
    toolbar_bg: '#ffffff',
    background: '#ffffff',
  },
}

function loadTradingViewScript(): Promise<void> {
  if (window.TradingView) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TV_SCRIPT_SRC}"]`,
    )
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('TradingView script failed to load')),
      )
      return
    }

    const script = document.createElement('script')
    script.src = TV_SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () =>
      reject(new Error('TradingView script failed to load'))
    document.head.appendChild(script)
  })

  return scriptPromise
}

function applyWidgetTheme(
  widget: TradingViewWidget,
  container: HTMLElement | null,
  mode: ThemeMode,
): void {
  const colors = THEME_COLORS[mode]
  if (typeof widget.changeTheme === 'function') {
    // Preserves drawings, indicators, and chart state
    widget.changeTheme(colors.theme)
  }

  const iframe =
    widget.iframe ?? container?.querySelector('iframe') ?? undefined
  if (iframe) {
    iframe.style.backgroundColor = colors.background
  }
}

export interface TradingViewChartProps {
  symbol?: string
  interval?: string
  locale?: string
  className?: string
  style?: CSSProperties
  /** Override theme; defaults to app ThemeContext. */
  theme?: ThemeMode
}

/**
 * TradingView Advanced Chart — theme-reactive without remounting.
 * Symbol / interval / theme updates keep indicators & drawings intact.
 */
export function TradingViewChart({
  symbol = 'BINANCE:BTCUSDT',
  interval = '15',
  locale = 'en',
  className,
  style,
  theme: themeProp,
}: TradingViewChartProps) {
  const { theme: contextTheme } = useTheme()
  const theme = themeProp ?? contextTheme
  const reactId = useId().replace(/:/g, '')
  const containerId = `tv_${reactId}`
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<TradingViewWidget | null>(null)
  const readyRef = useRef(false)
  const pendingThemeRef = useRef<ThemeMode>(theme)
  const symbolRef = useRef(symbol)
  const intervalRef = useRef(interval)

  symbolRef.current = symbol
  intervalRef.current = interval
  pendingThemeRef.current = theme

  // Mount once — do not remount on theme/symbol changes
  useEffect(() => {
    let cancelled = false
    const container = containerRef.current
    if (!container) return

    void (async () => {
      try {
        await loadTradingViewScript()
        if (cancelled || !window.TradingView) return

        container.innerHTML = ''
        const initial = THEME_COLORS[pendingThemeRef.current]

        widgetRef.current = new window.TradingView.widget({
          container_id: containerId,
          autosize: true,
          symbol: symbolRef.current,
          interval: intervalRef.current,
          timezone: 'Etc/UTC',
          theme: initial.theme,
          style: '1',
          locale,
          toolbar_bg: initial.toolbar_bg,
          enable_publishing: false,
          hide_top_toolbar: false,
          hide_legend: false,
          save_image: false,
          withdateranges: true,
          allow_symbol_change: true,
          details: false,
          hotlist: false,
          calendar: false,
          studies: [],
        })

        widgetRef.current.onChartReady(() => {
          if (cancelled) return
          readyRef.current = true
          // Apply any theme toggled while the widget was booting
          applyWidgetTheme(
            widgetRef.current!,
            containerRef.current,
            pendingThemeRef.current,
          )
          // Sync symbol if it changed during init
          try {
            widgetRef.current?.activeChart?.()?.setSymbol(
              symbolRef.current,
              intervalRef.current,
            )
          } catch {
            /* chart API may not expose setSymbol on all builds */
          }
        })
      } catch (err) {
        console.error('[TradingView]', err)
      }
    })()

    return () => {
      cancelled = true
      readyRef.current = false
      try {
        widgetRef.current?.remove()
      } catch {
        /* already torn down */
      }
      widgetRef.current = null
      if (container) container.innerHTML = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, locale])

  // Instant dark/light sync — no remount
  useEffect(() => {
    pendingThemeRef.current = theme
    const widget = widgetRef.current
    if (!widget || !readyRef.current) return
    applyWidgetTheme(widget, containerRef.current, theme)
  }, [theme])

  // Symbol / interval without remount
  useEffect(() => {
    if (!readyRef.current) return
    try {
      widgetRef.current?.activeChart?.()?.setSymbol(symbol, interval)
    } catch {
      /* ignore */
    }
  }, [symbol, interval])

  // Resize with split-pane / flex parents
  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 200,
        ...style,
      }}
    >
      <div
        id={containerId}
        ref={containerRef}
        className="h-full w-full overflow-hidden rounded-sm bg-terminal-bg"
      />
    </div>
  )
}
